import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Shield, CheckCircle2, AlertTriangle, FileText, Lock, Eye, Download, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicPAFPage() {
  const { token } = useParams();
  const [paf, setPaf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/public/paf/${token}`);
        setPaf(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'PAF not found or access disabled');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleViewDoc = (doc) => {
    const url = `${API}/public/paf/${token}/documents/${doc.id}`;
    window.open(url, '_blank');
  };

  const handleDownload = (doc) => {
    const url = `${API}/public/paf/${token}/documents/${doc.id}?download=1`;
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.original_filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 200);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFF]">
        <div className="w-8 h-8 border-2 border-[#4F46E5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFF]">
        <div className="text-center">
          <Lock className="w-12 h-12 text-[#64748B] mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="text-xl font-semibold text-[#0F172A] mb-2">Access Denied</h1>
          <p className="text-[#64748B]">{error}</p>
        </div>
      </div>
    );
  }

  const emp = paf.employee || {};
  const DOC_TYPES = [
    { key: 'lca', label: 'Labor Condition Application (LCA)', description: 'Certified LCA from DOL' },
    { key: 'wage_documentation', label: 'Wage Documentation', description: 'Actual wage and prevailing wage records' },
    { key: 'wage_determination', label: 'Wage Determination Explanation', description: 'Methodology for wage determination' },
    { key: 'employee_notification', label: 'Employee Notification Proof', description: 'Notice to employee of LCA filing' },
    { key: 'benefits_summary', label: 'Benefits Summary', description: 'Summary of benefits offered to H-1B, H-1B1, and E-3 workers' },
  ];

  const docsByType = {};
  (paf.documents || []).forEach(doc => {
    if (!docsByType[doc.doc_type]) docsByType[doc.doc_type] = [];
    docsByType[doc.doc_type].push(doc);
  });

  const toggleExpand = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const statusColor = {
    compliant: { bg: '#ECFDF5', text: '#059669', label: 'Compliant' },
    warning: { bg: '#FFFBEB', text: '#D97706', label: 'Warning' },
    non_compliant: { bg: '#FEF2F2', text: '#DC2626', label: 'Non-Compliant' },
  }[paf.compliance_status] || { bg: '#FEF2F2', text: '#DC2626', label: 'Non-Compliant' };

  return (
    <div className="min-h-screen bg-[#F8FAFF]" data-testid="public-paf-page">
      {/* Header */}
      <div className="backdrop-blur-xl bg-white/70 border-b border-indigo-100/50 sticky top-0 z-30 px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Shield className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.5} />
          <span className="font-semibold text-[#0F172A] tracking-tight">PAFHub</span>
          <span className="text-xs text-[#64748B] ml-auto">Public Access File — Read Only</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-8 px-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {/* Employee Info */}
          <div className="bg-white rounded-2xl border border-indigo-100/50 p-6 md:p-8 shadow-sm mb-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-[#0F172A] tracking-tight mb-1">
                  {emp.first_name} {emp.last_name}
                </h1>
                <p className="text-[#64748B]">{emp.job_title} — {emp.department}</p>
              </div>
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: statusColor.bg, color: statusColor.text }}>
                {statusColor.label}
              </span>
            </div>
          </div>

          {/* Progress */}
          <div className="bg-white rounded-2xl border border-indigo-100/50 p-6 shadow-sm mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-[#0F172A]">Compliance Progress</h2>
              <span className="text-sm font-medium text-[#0F172A]">{paf.completion_percentage}%</span>
            </div>
            <Progress value={paf.completion_percentage} className="h-3 bg-[#F8FAFF]" />
          </div>

          {/* Document Checklist with Inline Documents */}
          <div className="bg-white rounded-2xl border border-indigo-100/50 p-6 md:p-8 shadow-sm mb-6">
            <h2 className="text-lg font-semibold text-[#0F172A] tracking-tight mb-4">Document Checklist</h2>
            <div className="space-y-3">
              {DOC_TYPES.map(({ key, label, description }) => {
                const hasDoc = paf.checklist?.[key];
                const typeDocs = docsByType[key] || [];
                const isExpanded = expanded[key];

                return (
                  <div key={key} data-testid={`public-checklist-${key}`}>
                    <button
                      onClick={() => typeDocs.length > 0 && toggleExpand(key)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left ${
                        typeDocs.length > 0 ? 'cursor-pointer hover:bg-indigo-50/60' : 'cursor-default'
                      } ${isExpanded ? 'bg-indigo-50/50' : 'bg-[#F8FAFF]'}`}
                      data-testid={`public-checklist-toggle-${key}`}
                    >
                      <div className="flex-shrink-0">
                        {hasDoc ? (
                          <CheckCircle2 className="w-5 h-5 text-[#10B981]" strokeWidth={1.5} />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-[#64748B]" strokeWidth={1.5} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${hasDoc ? 'text-[#0F172A]' : 'text-[#64748B]'}`}>{label}</p>
                        <p className="text-xs text-[#94A3B8] mt-0.5">{description}</p>
                      </div>
                      {typeDocs.length > 0 && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <ChevronDown className={`w-4 h-4 text-[#64748B] transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} strokeWidth={1.5} />
                        </div>
                      )}
                      {typeDocs.length === 0 && (
                        <span className="text-xs text-[#94A3B8] flex-shrink-0">No document</span>
                      )}
                    </button>

                    <AnimatePresence>
                      {isExpanded && typeDocs.length > 0 && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="ml-8 mt-2 space-y-2">
                            {typeDocs.map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between p-3 bg-white border border-indigo-100/40 rounded-xl" data-testid={`public-doc-${doc.id}`}>
                                <div className="flex items-center gap-3 min-w-0">
                                  <FileText className="w-4 h-4 text-[#4F46E5] flex-shrink-0" strokeWidth={1.5} />
                                  <div className="min-w-0">
                                    <p className="text-sm text-[#0F172A] font-medium truncate">{doc.original_filename}</p>
                                    <p className="text-xs text-[#94A3B8]">
                                      {doc.size_bytes ? `${(doc.size_bytes / 1024).toFixed(1)} KB · ` : ''}
                                      {new Date(doc.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    onClick={() => handleViewDoc(doc)}
                                    className="p-1.5 rounded-lg hover:bg-[#F8FAFF] text-[#64748B] hover:text-[#4F46E5] transition-colors"
                                    title="View"
                                    data-testid={`public-view-doc-${doc.id}`}
                                  >
                                    <Eye className="w-4 h-4" strokeWidth={1.5} />
                                  </button>
                                  <button
                                    onClick={() => handleDownload(doc)}
                                    className="p-1.5 rounded-lg hover:bg-[#F8FAFF] text-[#64748B] hover:text-[#4F46E5] transition-colors"
                                    title="Download"
                                    data-testid={`public-download-doc-${doc.id}`}
                                  >
                                    <Download className="w-4 h-4" strokeWidth={1.5} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
